import React from "react";
import moment from "moment";
import DashUploadDocument from "./DashUploadDocument";

export interface QueryTrayProps {
  items: any[];
  base: any[];
  expandedId: string;
  categoriesList: any[];
  deeds: any[];
  isNewFile: boolean;
  isbtnload: boolean;
  dashboardState: any;
  onAddExtraDocument: () => void;
  onAddUploadDocumentForTray: (query: any) => void;
  onToggleExpand: (expandKey: string) => void;
  onOpenHint: (query: any) => void;
  onRemoveTrayDocument: (trayId: string, docIndex: number) => void;
  onDocumentDetailsChange: any;
  onDocumentFilesChange: any;
  onDeleteDocumentDetails: any;
  onDeleteFile: any;
  previewDoc: (url: string) => void;
}

const getTrayQueryRowId = (query: any, index: number) => String(query?.id || `tray-row-${index}`);

const formatTrayDateTime = (value: string, formatted?: string) => {
  if (formatted && String(formatted).trim()) return formatted;
  if (!value) return "-";
  const parsed = moment(value, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY HH:mm:ss", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY hh:mm A") : value;
};

const getTrayRaisedDateParts = (query: any) => {
  const raisedValue = query?.query_raised_date || query?.created_date || "";
  const formatted = formatTrayDateTime(raisedValue, query?.raisedOn);
  const parsed = moment(raisedValue, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY HH:mm:ss", moment.ISO_8601], true);
  if (parsed.isValid()) {
    return { date: parsed.format("DD-MM-YYYY"), time: parsed.format("hh:mm A") };
  }
  const parts = String(formatted).split(" ");
  return { date: parts.slice(0, 1).join(" ") || "-", time: parts.slice(1).join(" ") || "-" };
};

const QueryTray: React.FC<QueryTrayProps> = ({
  items,
  base,
  expandedId,
  categoriesList,
  deeds,
  isNewFile,
  isbtnload,
  dashboardState,
  onAddExtraDocument,
  onAddUploadDocumentForTray,
  onToggleExpand,
  onOpenHint,
  onRemoveTrayDocument,
  onDocumentDetailsChange,
  onDocumentFilesChange,
  onDeleteDocumentDetails,
  onDeleteFile,
  previewDoc,
}) => {
  const renderTrayRows = () =>
    items.map((query: any, index: number) => {
      const queryRowId = getTrayQueryRowId(query, index);
      const expandKey = String(query?.id || index);
      const isExpanded = expandedId === expandKey;
      const raisedParts = getTrayRaisedDateParts(query);
      const mappedUploadDocs = Array.isArray(base)
        ? base
            .map((document: any, docIndex: number) => ({ document, docIndex }))
            .filter(
              ({ document }: any) =>
                String(document?.tray_query_id || document?.query_id || "") === String(query?.id || "")
            )
        : [];

      return (
        <React.Fragment key={`tray-upload-query-${queryRowId}`}>
          <tr className="query-client-row">
            <td className="query-upload-sl-col">
              <div className="query-upload-sl-cell">
                <span className="query-upload-sl-num">{index + 1}</span>
                <span className="query-row-dot dot-received"></span>
              </div>
            </td>
            <td className="query-upload-query-col">
              <div className="query-client-main">{query?.title || "-"}</div>
              {query?.location && String(query.location).trim() && String(query.location).trim() !== "-" ? (
                <div className="query-client-sub">{query.location}</div>
              ) : null}
            </td>
            <td>{query?.reason || "-"}</td>
            <td>
              <div className="query-client-main">{raisedParts.date}</div>
              <div className="query-client-sub">{raisedParts.time}</div>
            </td>
            <td>
              <span className="query-client-pill query-client-pill-received">Query Received</span>
            </td>
            <td>
              <div className="query-client-actions">
                <button
                  type="button"
                  className="query-client-requery-btn query-upload-action-btn query-upload-action-upload"
                  title="Map Query For Upload"
                  onClick={() => onAddUploadDocumentForTray(query)}
                >
                  Upload
                </button>
                <button
                  type="button"
                  className="query-client-requery-btn query-upload-action-btn query-upload-action-hint"
                  title="Add Hint"
                  onClick={() => onOpenHint(query)}
                >
                  Hint
                </button>
                <button
                  type="button"
                  className="icon-btn query-upload-expand-btn"
                  title="Expand"
                  onClick={() => onToggleExpand(expandKey)}
                >
                  <i className={`fa ${isExpanded ? "fa-angle-up" : "fa-angle-down"}`} aria-hidden="true"></i>
                </button>
              </div>
            </td>
          </tr>
          {isExpanded ? (
            <tr className="query-client-expanded-row">
              <td colSpan={6}>
                <div className="query-client-expanded">
                  <div style={{ marginBottom: "12px", fontSize: "13px", color: "#64748b" }}>
                    <strong>Saved in Tray:</strong> {formatTrayDateTime(query?.saved_dt, query?.savedOn)}
                    {query?.savedBy ? (
                      <>
                        {" "}
                        by <strong>{query.savedBy}</strong>
                      </>
                    ) : null}
                  </div>
                  {Array.isArray(query?.partialDocuments) && query.partialDocuments.length > 0 ? (
                    <div
                      style={{
                        border: "1px solid #dbeafe",
                        borderRadius: "10px",
                        background: "#f8fbff",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "#eff6ff",
                          borderBottom: "1px solid #dbeafe",
                          fontWeight: 600,
                          color: "#1e40af",
                        }}
                      >
                        Partially Received Documents
                      </div>
                      <div style={{ padding: "8px 12px" }}>
                        {query.partialDocuments.map((doc: any, docIndex: number) => (
                          <div
                            key={`tray-partial-doc-${query?.id}-${docIndex}`}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "6px 10px",
                              border: "1px solid #bfdbfe",
                              borderRadius: "8px",
                              marginBottom: docIndex === query.partialDocuments.length - 1 ? 0 : "6px",
                              background: "#fff",
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {docIndex + 1}. {doc?.documentType || doc?.category || "Document"}
                              </div>
                              <div style={{ fontSize: "12px", color: "#64748b" }}>
                                {doc?.category || "-"} | {doc?.docNoYear || "-"} | {doc?.particular || "-"}
                              </div>
                              {doc?.savedOn || doc?.savedBy ? (
                                <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                                  Saved: {doc?.savedOn || "-"}
                                  {doc?.savedBy ? ` by ${doc.savedBy}` : ""}
                                </div>
                              ) : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              {doc?.docUrl ? (
                                <button
                                  type="button"
                                  className="icon-btn"
                                  title="View document"
                                  data-toggle="modal"
                                  data-target="#preview-document"
                                  onClick={() => previewDoc(doc.docUrl)}
                                >
                                  <i className="fa fa-eye" aria-hidden="true"></i>
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="icon-btn query-tray-remove-btn"
                                title="Remove document"
                                onClick={() => onRemoveTrayDocument(query?.id, docIndex)}
                              >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {Array.isArray(query?.partialHints) && query.partialHints.length > 0 ? (
                    <div
                      style={{
                        border: "1px solid #fde68a",
                        borderRadius: "10px",
                        background: "#fffbeb",
                        overflow: "hidden",
                        marginTop: query?.partialDocuments?.length ? "12px" : 0,
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "#fef3c7",
                          borderBottom: "1px solid #fde68a",
                          fontWeight: 600,
                          color: "#92400e",
                        }}
                      >
                        Query Hints
                      </div>
                      <div style={{ padding: "8px 12px" }}>
                        {query.partialHints.map((hint: any, hintIndex: number) => (
                          <div
                            key={`tray-partial-hint-${query?.id}-${hintIndex}`}
                            style={{
                              padding: "6px 10px",
                              borderRadius: "8px",
                              background: "#ffffff",
                              border: "1px solid #fde68a",
                              marginBottom: hintIndex === query.partialHints.length - 1 ? 0 : "6px",
                            }}
                          >
                            <span style={{ fontWeight: 600, marginRight: "6px", color: "#92400e" }}>
                              {hintIndex + 1}.
                            </span>
                            <span style={{ color: "#111827" }}>{hint?.hint || hint?.hintText || "-"}</span>
                            {hint?.savedOn || hint?.savedBy ? (
                              <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>
                                Saved: {hint?.savedOn || "-"}
                                {hint?.savedBy ? ` by ${hint.savedBy}` : ""}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </td>
            </tr>
          ) : null}
          {mappedUploadDocs.map(({ document, docIndex }: any) => (
            <React.Fragment key={`mapped-upload-row-${query?.id || index}-${docIndex}`}>
              <tr className="query-client-expanded-row">
                <td colSpan={6}>
                  <DashUploadDocument
                    key={docIndex}
                    baseDetails={document}
                    handleDocumentDetailsChange={onDocumentDetailsChange}
                    handleDocumentFilesChange={onDocumentFilesChange}
                    deleteDocumentDetails={onDeleteDocumentDetails}
                    deleteFile={onDeleteFile}
                    deedList={deeds}
                    previewDoc={previewDoc}
                    categoriesList={categoriesList}
                    index={docIndex}
                    newfile={isNewFile}
                    path="filedashboard"
                    state={dashboardState}
                    isbtnload={isbtnload}
                  />
                </td>
              </tr>
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    });

  return (
    <div className="mt-4 query-client-given-wrapper query-tray-wrapper query-upload-modal-section">
      <div className="query-upload-section-head query-upload-section-head-tray">
        <div className="query-upload-section-title">
          <h6 className="mb-0">Query Tray</h6>
          <p className="query-tray-helper-text">
            Partially received query documents saved for later upload or mail.
          </p>
        </div>
        <div className="query-upload-section-actions query-upload-section-actions-tray">
          <button
            type="button"
            className="btn btn-primary btn-sm upload-document-button query-tray-add-doc-btn"
            onClick={onAddExtraDocument}
          >
            Add Extra Document +
          </button>
        </div>
      </div>
      <div className="table-responsive query-upload-table-wrap">
        <table className="table query-client-given-table query-upload-modal-table">
          <thead>
            <tr>
              <th className="query-upload-sl-col">SL</th>
              <th className="query-upload-query-col">Query</th>
              <th>Reason</th>
              <th>Raised On</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              renderTrayRows()
            ) : (
              <tr>
                <td colSpan={6} className="text-center">
                  No documents in query tray
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QueryTray;
